/**
 * DesktopOnly.tsx — gates the staff surfaces (/admin, /clinicians) off mobile.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the staff console (clinician review, admin pricing/invites) is a dense,
 * desktop workflow — patients are the only role expected on a phone. Sits
 * OUTSIDE RequireRole in App.tsx so the mobile notice replaces the sign-in and
 * unauthorised screens too, not just the authenticated content.
 *
 * jsdom (and any environment without `window.matchMedia`) has no viewport to
 * measure, so we default to desktop there — every existing spec that renders
 * /admin or /clinicians keeps passing without stubbing matchMedia.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PageFrame, Title, Help, ButtonArrow, buttonClass, buttonStyle } from '../ui/kit';

const MOBILE_QUERY = '(max-width: 767px)';

function matchesMobile(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(MOBILE_QUERY).matches;
}

/** Render `children` only on a desktop-sized viewport; otherwise a friendly notice. */
export function DesktopOnly({ children }: { children: ReactNode }) {
  const [isMobile, setIsMobile] = useState(matchesMobile);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  if (isMobile) {
    return (
      <PageFrame narrow>
        <Title>Please use a bigger screen</Title>
        <Help>The staff area needs a bigger screen — please use a desktop or laptop.</Help>
        <Link to="/" className={buttonClass('cta')} style={buttonStyle('cta')}>
          Back to GPS Clinics
          <ButtonArrow />
        </Link>
      </PageFrame>
    );
  }

  return <>{children}</>;
}
